import api from './client';
import { ReportCapability, ReportType } from '@/pages/reports/ReportsPage/types';

export interface ReportBrowseQuery {
  q?: string;
  type?: ReportType;
  playerId?: string;
  authorId?: string;
  from?: string;
  to?: string;
  minRating?: number;
  maxRating?: number;
  medicalStatus?: string;
  medicalSeverity?: string;
  hasAttachment?: boolean;
  sort?: 'date' | 'player' | 'author' | 'rating';
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: 20 | 50 | 100;
}

export interface ReportSummaryDTO {
  id: string;
  type: ReportType;
  player: { id: string; name: string };
  author: { id: string; name: string };
  date: string;
  rating: number | null;
  medicalStatus: string | null;
  medicalSeverity: string | null;
  attachmentCount: number;
  capabilities: ReportCapability[];
}

export interface ReportsBrowseDTO {
  data: ReportSummaryDTO[];
  meta: { page: number; page_size: number; total: number; total_pages: number };
  facets: { players: Array<{ id: string; name: string; count: number }>; authors: Array<{ id: string; name: string; count: number }> };
  capabilities: ReportCapability[];
}

export interface ReportDetailDTO {
  id: string;
  type: ReportType;
  player: { id: string; name: string; href: string };
  author: { id: string; name: string };
  date: string;
  status: string | null;
  headlineMetrics: Array<{ label: string; value: string }>;
  attachmentCount: number;
  revisionCount: number;
  capabilities: ReportCapability[];
  content: Record<string, unknown>;
}

export interface ReportRevisionDTO {
  id: string;
  author: { id: string; name: string };
  status: string;
  severity: string;
  notes: string | null;
  isRecurrent: boolean;
  expectedReturnWeeks: number | null;
  expectedReturnDate: string | null;
  createdAt: string;
}

export interface ReportAttachmentDTO {
  id: string;
  title: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploader: { id: string; name: string };
  createdAt: string;
  capabilities: ReportCapability[];
}

const browseParams = (query: ReportBrowseQuery): Record<string, unknown> => ({
  ...(query.q ? { q: query.q } : {}),
  ...(query.type ? { type: query.type } : {}),
  ...(query.playerId ? { player_id: query.playerId } : {}),
  ...(query.authorId ? { author_id: query.authorId } : {}),
  ...(query.from ? { from: query.from } : {}),
  ...(query.to ? { to: query.to } : {}),
  ...(query.minRating !== undefined ? { min_rating: query.minRating } : {}),
  ...(query.maxRating !== undefined ? { max_rating: query.maxRating } : {}),
  ...(query.medicalStatus ? { medical_status: query.medicalStatus } : {}),
  ...(query.medicalSeverity ? { medical_severity: query.medicalSeverity } : {}),
  ...(query.hasAttachment !== undefined ? { has_attachment: query.hasAttachment } : {}),
  ...(query.sort && query.sort !== 'date' ? { sort: query.sort } : {}),
  ...(query.order && query.order !== 'desc' ? { order: query.order } : {}),
  ...(query.page && query.page !== 1 ? { page: query.page } : {}),
  ...(query.pageSize && query.pageSize !== 20 ? { page_size: query.pageSize } : {}),
});

export const reportKeys = {
  root: (workspaceId: string) => ['reports-v2', workspaceId] as const,
  browse: (workspaceId: string, query: ReportBrowseQuery) => [...reportKeys.root(workspaceId), 'browse', query] as const,
  detail: (workspaceId: string, type: ReportType, id: string) => [...reportKeys.root(workspaceId), 'detail', type, id] as const,
  revisions: (workspaceId: string, id: string, page?: number) => [...reportKeys.root(workspaceId), 'revisions', id, ...(page === undefined ? [] : [page])] as const,
  attachments: (workspaceId: string, type: ReportType, id: string) => [...reportKeys.root(workspaceId), 'attachments', type, id] as const,
  options: (workspaceId: string, type: ReportType) => [...reportKeys.root(workspaceId), 'options', type] as const,
};

const workspaceHeaders = (workspaceId: string) => ({ headers: { 'X-Workspace-ID': workspaceId } });

export const reportsV2API = {
  options: async (workspaceId: string, type: ReportType): Promise<{ data: { players: Array<{ id: string; name: string }> }; capabilities: ReportCapability[] }> => (await api.get('/v2/reports/options', { ...workspaceHeaders(workspaceId), params: { type } })).data,
  browse: async (workspaceId: string, query: ReportBrowseQuery): Promise<ReportsBrowseDTO> => (await api.get('/v2/reports', { ...workspaceHeaders(workspaceId), params: browseParams(query) })).data,
  detail: async (workspaceId: string, type: ReportType, id: string): Promise<ReportDetailDTO> => (await api.get(`/v2/reports/${type}/${id}`, workspaceHeaders(workspaceId))).data.data,
  create: async <T>(workspaceId: string, type: ReportType, body: T): Promise<ReportDetailDTO> => (await api.post(`/v2/reports/${type}`, body, workspaceHeaders(workspaceId))).data.data,
  update: async <T>(workspaceId: string, type: ReportType, id: string, body: T): Promise<ReportDetailDTO> => (await api.patch(`/v2/reports/${type}/${id}`, body, workspaceHeaders(workspaceId))).data.data,
  delete: async (workspaceId: string, type: ReportType, id: string): Promise<void> => { await api.delete(`/v2/reports/${type}/${id}`, workspaceHeaders(workspaceId)); },
  revisions: async (workspaceId: string, id: string, page = 1): Promise<{ data: ReportRevisionDTO[]; meta: ReportsBrowseDTO['meta'] }> => (await api.get(`/v2/reports/medical/${id}/revisions`, { ...workspaceHeaders(workspaceId), params: page === 1 ? {} : { page } })).data,
  createRevision: async <T>(workspaceId: string, id: string, body: T): Promise<ReportRevisionDTO> => (await api.post(`/v2/reports/medical/${id}/revisions`, body, workspaceHeaders(workspaceId))).data.data,
  attachments: async (workspaceId: string, type: ReportType, id: string): Promise<ReportAttachmentDTO[]> => (await api.get(`/v2/reports/${type}/${id}/attachments`, workspaceHeaders(workspaceId))).data.data,
  uploadAttachment: async (workspaceId: string, type: ReportType, id: string, file: File, title?: string, signal?: AbortSignal): Promise<ReportAttachmentDTO> => {
    const body = new FormData();
    if (title) body.append('title', title);
    body.append('file', file);
    return (await api.post(`/v2/reports/${type}/${id}/attachments`, body, { headers: { 'Content-Type': 'multipart/form-data', 'X-Workspace-ID': workspaceId }, signal })).data.data;
  },
  downloadAttachment: async (workspaceId: string, type: ReportType, id: string, attachmentId: string): Promise<Blob> => (await api.get(`/v2/reports/${type}/${id}/attachments/${attachmentId}`, { ...workspaceHeaders(workspaceId), responseType: 'blob' })).data,
  deleteAttachment: async (workspaceId: string, type: ReportType, id: string, attachmentId: string): Promise<void> => { await api.delete(`/v2/reports/${type}/${id}/attachments/${attachmentId}`, workspaceHeaders(workspaceId)); },
  exportBrowse: async (workspaceId: string, query: ReportBrowseQuery): Promise<Blob> => (await api.get('/v2/reports/export', { ...workspaceHeaders(workspaceId), params: browseParams(query), responseType: 'blob' })).data,
  exportDetail: async (workspaceId: string, type: ReportType, id: string): Promise<Blob> => (await api.get(`/v2/reports/${type}/${id}/export`, { ...workspaceHeaders(workspaceId), responseType: 'blob' })).data,
};
